/**
 * ControllingZeitKosten — Zeit & Kosten Auswertung (#992).
 *
 * 5 Sub-Tabs in Anzeige-Reihenfolge:
 *   1) Profitabilitaets-Heatmap (Kunde x Monat)
 *   2) MA-Auslastung
 *   3) Trends (Wochen/Monate)
 *   4) Quick-Insights
 *   5) Was-waere-wenn-Calculator
 *
 * @page /controlling/zeit-kosten
 * @since v2.429.0
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { Download, RefreshCw, TrendingUp, TrendingDown } from 'lucide-react';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
  BarChart, Bar, PieChart, Pie, Cell, Legend
} from 'recharts';
import { useLanguage } from '../context/LanguageContext';

const api = (path) => fetch(path, { credentials: 'include' }).then(r => r.ok ? r.json() : r.json().then(e => Promise.reject(e)));

const fmt0 = (n) => n != null ? Number(n).toLocaleString('de-AT', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) : '—';
const fmt1 = (n) => n != null ? Number(n).toLocaleString('de-AT', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) : '—';
const fmt2 = (n) => n != null ? Number(n).toLocaleString('de-AT', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—';
const fmtEUR = (n) => n == null ? '—' : Math.abs(n) >= 10
  ? '€ ' + Number(n).toLocaleString('de-AT', { minimumFractionDigits: 0, maximumFractionDigits: 0 })
  : '€ ' + Number(n).toLocaleString('de-AT', { minimumFractionDigits: 1, maximumFractionDigits: 1 });

const MONAT_KURZ = ['', 'Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'];

const TABS = [
  { key: 'heatmap',    labelKey: 'controllingZeitKosten.tabs.heatmap' },
  { key: 'ma',         labelKey: 'controllingZeitKosten.tabs.ma' },
  { key: 'trends',     labelKey: 'controllingZeitKosten.tabs.trends' },
  { key: 'insights',   labelKey: 'controllingZeitKosten.tabs.insights' },
  { key: 'calculator', labelKey: 'controllingZeitKosten.tabs.calculator' },
];

const KAT_FARBEN = ['#338edc', '#22D3EE', '#6366F1', '#F59E0B', '#10B981', '#EC4899', '#8B5CF6', '#EF4444'];

function defaultRange() {
  const now = new Date();
  const yyyy = now.getFullYear();
  const von = `${yyyy}-01-01`;
  const bis = now.toISOString().slice(0, 10);
  return { von, bis };
}

// ────────────────────────── Tab 1: Profitabilitaets-Heatmap ──────────────────────────
function HeatmapTab({ t, range }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    setLoading(true);
    try { setData(await api(`/api/controlling/zeit-kosten/heatmap?von=${range.von}&bis=${range.bis}`)); }
    catch (e) { console.error(e); setData(null); }
    finally { setLoading(false); }
  }, [range.von, range.bis]);

  useEffect(() => { reload(); }, [reload]);

  // Cells -> Map[kunde][monat]
  const grid = useMemo(() => {
    if (!data?.cells) return { kunden: [], monate: [], cells: {} };
    const map = {};
    const kundenSet = new Map();
    for (const c of data.cells) {
      const key = `${c.kunde_id || 0}_${c.monat}`;
      map[key] = c;
      if (!kundenSet.has(c.kunde_id || 0)) {
        kundenSet.set(c.kunde_id || 0, c.kunde_name);
      }
    }
    return {
      kunden: Array.from(kundenSet.entries()).map(([id, name]) => ({ id, name })),
      monate: data.monate || [],
      cells: map,
    };
  }, [data]);

  function colorFor(margePct) {
    if (margePct == null) return 'var(--bg-inset,#F8F9FC)';
    if (margePct < 0) return '#FEE2E2';
    if (margePct < 10) return '#FEF3C7';
    if (margePct < 20) return '#DBEAFE';
    return '#D1FAE5';
  }

  function downloadCsv() {
    window.location.href = `/api/controlling/zeit-kosten/export?von=${range.von}&bis=${range.bis}`;
  }

  if (loading) return <div style={{ padding: 24 }}>{t('common.loading')}</div>;
  if (!grid.kunden.length) return <EmptyState t={t} />;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ fontSize: 13, color: 'var(--text-tertiary,#6B7280)' }}>
          {t('controllingZeitKosten.heatmap.hint')}
        </div>
        <button onClick={downloadCsv}
          style={{ padding: '6px 14px', borderRadius: 8, border: '1px solid var(--border-card,#E5E7EB)', background: 'var(--bg-card,#fff)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
          <Download size={14} /> {t('controllingZeitKosten.heatmap.export')}
        </button>
      </div>
      <div style={{ overflowX: 'auto', border: '1px solid var(--border-card,#E5E7EB)', borderRadius: 12, background: 'var(--bg-card,#fff)' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--border-card,#E5E7EB)' }}>
              <th style={{ padding: '10px 12px', textAlign: 'left', position: 'sticky', left: 0, background: 'var(--bg-card,#fff)', minWidth: 200 }}>
                {t('controllingZeitKosten.heatmap.kunde')}
              </th>
              {grid.monate.map(m => (
                <th key={m} style={{ padding: '10px 8px', textAlign: 'center', fontSize: 12, fontWeight: 600, color: 'var(--text-secondary,#4B5563)', minWidth: 90 }}>
                  {(() => { const [y, mm] = m.split('-'); return `${MONAT_KURZ[Number(mm)]} ${y.slice(2)}`; })()}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {grid.kunden.map(k => (
              <tr key={k.id} style={{ borderBottom: '1px solid var(--border-card,#F3F4F6)' }}>
                <td style={{ padding: '10px 12px', fontWeight: 500, color: 'var(--text-primary,#1A1A2E)', position: 'sticky', left: 0, background: 'var(--bg-card,#fff)' }}>
                  {k.name}
                </td>
                {grid.monate.map(m => {
                  const c = grid.cells[`${k.id}_${m}`];
                  if (!c) return <td key={m} style={{ padding: '8px', textAlign: 'center', color: 'var(--text-muted,#9CA3AF)' }}>—</td>;
                  return (
                    <td key={m} style={{ padding: 0, textAlign: 'center' }}>
                      <div style={{
                        margin: 4, padding: '6px 8px', borderRadius: 6,
                        background: colorFor(c.marge_pct),
                        fontSize: 12, lineHeight: 1.3,
                      }}>
                        <div style={{ fontWeight: 600 }}>{fmt1(c.stunden)}h</div>
                        <div style={{ color: 'var(--text-secondary,#4B5563)', fontSize: 11 }}>{fmtEUR(c.kosten_intern)}</div>
                        {c.marge_pct != null && (
                          <div style={{ fontSize: 10, color: c.marge_pct < 0 ? '#991B1B' : 'var(--text-tertiary,#6B7280)', fontWeight: 500 }}>
                            {fmt1(c.marge_pct)}%
                          </div>
                        )}
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{ display: 'flex', gap: 16, marginTop: 12, fontSize: 12, color: 'var(--text-tertiary,#6B7280)' }}>
        <span><span style={{ display: 'inline-block', width: 12, height: 12, background: '#FEE2E2', borderRadius: 3, marginRight: 4, verticalAlign: 'middle' }}/> {t('controllingZeitKosten.heatmap.legendNeg')}</span>
        <span><span style={{ display: 'inline-block', width: 12, height: 12, background: '#FEF3C7', borderRadius: 3, marginRight: 4, verticalAlign: 'middle' }}/> &lt; 10%</span>
        <span><span style={{ display: 'inline-block', width: 12, height: 12, background: '#DBEAFE', borderRadius: 3, marginRight: 4, verticalAlign: 'middle' }}/> 10–20%</span>
        <span><span style={{ display: 'inline-block', width: 12, height: 12, background: '#D1FAE5', borderRadius: 3, marginRight: 4, verticalAlign: 'middle' }}/> &gt; 20%</span>
      </div>
    </div>
  );
}

// ────────────────────────── Tab 2: MA-Auslastung ──────────────────────────
function MaTab({ t, range }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api(`/api/controlling/zeit-kosten/ma-auslastung?von=${range.von}&bis=${range.bis}`)
      .then(setData).catch(() => setData(null)).finally(() => setLoading(false));
  }, [range.von, range.bis]);

  if (loading) return <div style={{ padding: 24 }}>{t('common.loading')}</div>;
  if (!data?.items?.length) return <EmptyState t={t} />;

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 16 }}>
        <Kpi label={t('controllingZeitKosten.ma.totalHours')} value={`${fmt1(data.summary.std_gesamt)}h`} />
        <Kpi label={t('controllingZeitKosten.ma.billableHours')} value={`${fmt1(data.summary.std_billable)}h`} />
        <Kpi label={t('controllingZeitKosten.ma.billablePct')} value={`${fmt1(data.summary.billable_pct)}%`} />
        <Kpi label={t('controllingZeitKosten.ma.totalCost')} value={fmtEUR(data.summary.kosten)} />
      </div>
      <div style={{ border: '1px solid var(--border-card,#E5E7EB)', borderRadius: 12, background: 'var(--bg-card,#fff)', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--border-card,#E5E7EB)', background: 'var(--bg-inset,#F8F9FC)' }}>
              <Th>{t('controllingZeitKosten.ma.col.name')}</Th>
              <Th align="right">{t('controllingZeitKosten.ma.col.rate')}</Th>
              <Th align="right">{t('controllingZeitKosten.ma.col.total')}</Th>
              <Th align="right">{t('controllingZeitKosten.ma.col.billable')}</Th>
              <Th align="right">{t('controllingZeitKosten.ma.col.billablePct')}</Th>
              <Th align="right">{t('controllingZeitKosten.ma.col.cost')}</Th>
              <Th align="right">{t('controllingZeitKosten.ma.col.kunden')}</Th>
            </tr>
          </thead>
          <tbody>
            {data.items.map(r => (
              <tr key={r.mitarbeiter_id} style={{ borderBottom: '1px solid var(--border-card,#F3F4F6)' }}>
                <Td>{r.ma_name}</Td>
                <Td align="right">€ {fmt2(r.stundensatz)}</Td>
                <Td align="right">{fmt1(r.std_gesamt)}h</Td>
                <Td align="right">{fmt1(r.std_billable)}h</Td>
                <Td align="right">
                  <span style={{ display: 'inline-block', minWidth: 50, padding: '2px 8px', borderRadius: 12, fontSize: 12, fontWeight: 500,
                    background: r.billable_pct >= 70 ? '#D1FAE5' : r.billable_pct >= 40 ? '#FEF3C7' : '#FEE2E2',
                    color: r.billable_pct >= 70 ? '#065F46' : r.billable_pct >= 40 ? '#92400E' : '#991B1B' }}>
                    {fmt1(r.billable_pct)}%
                  </span>
                </Td>
                <Td align="right">{fmtEUR(r.kosten)}</Td>
                <Td align="right">{r.kunden_anzahl}</Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ────────────────────────── Tab 3: Trends ──────────────────────────
function TrendsTab({ t, range }) {
  const [data, setData] = useState(null);
  const [granularity, setGranularity] = useState('woche');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api(`/api/controlling/zeit-kosten/trends?von=${range.von}&bis=${range.bis}&granularity=${granularity}`)
      .then(setData).catch(() => setData(null)).finally(() => setLoading(false));
  }, [range.von, range.bis, granularity]);

  if (loading) return <div style={{ padding: 24 }}>{t('common.loading')}</div>;
  if (!data?.items?.length) return <EmptyState t={t} />;

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', gap: 8 }}>
        <SegBtn active={granularity === 'woche'} onClick={() => setGranularity('woche')}>
          {t('controllingZeitKosten.trends.weekly')}
        </SegBtn>
        <SegBtn active={granularity === 'monat'} onClick={() => setGranularity('monat')}>
          {t('controllingZeitKosten.trends.monthly')}
        </SegBtn>
      </div>
      <div style={{ height: 360, padding: 16, border: '1px solid var(--border-card,#E5E7EB)', borderRadius: 12, background: 'var(--bg-card,#fff)' }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data.items}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
            <XAxis dataKey="bucket" tick={{ fontSize: 11 }} />
            <YAxis yAxisId="h" tick={{ fontSize: 11 }} label={{ value: 'h', angle: -90, position: 'insideLeft', fontSize: 11 }} />
            <YAxis yAxisId="e" orientation="right" tick={{ fontSize: 11 }} label={{ value: '€', angle: 90, position: 'insideRight', fontSize: 11 }} />
            <RechartsTooltip />
            <Legend />
            <Line yAxisId="h" type="monotone" dataKey="stunden" name={t('controllingZeitKosten.trends.hours')} stroke="#338edc" strokeWidth={2} dot={{ r: 3 }} />
            <Line yAxisId="h" type="monotone" dataKey="stunden_billable" name={t('controllingZeitKosten.trends.hoursBillable')} stroke="#22D3EE" strokeWidth={2} dot={{ r: 3 }} />
            <Line yAxisId="e" type="monotone" dataKey="kosten" name={t('controllingZeitKosten.trends.cost')} stroke="#6366F1" strokeWidth={2} dot={{ r: 3 }} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

// ────────────────────────── Tab 4: Quick-Insights ──────────────────────────
function InsightsTab({ t, range }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api(`/api/controlling/zeit-kosten/quick-insights?von=${range.von}&bis=${range.bis}`)
      .then(setData).catch(() => setData(null)).finally(() => setLoading(false));
  }, [range.von, range.bis]);

  if (loading) return <div style={{ padding: 24 }}>{t('common.loading')}</div>;
  if (!data?.totals) return <EmptyState t={t} />;

  const pieData = (data.kategorien || []).map((k, i) => ({
    name: k.name, value: k.stunden, fill: k.farbe || KAT_FARBEN[i % KAT_FARBEN.length]
  }));

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 16 }}>
        <Kpi label={t('controllingZeitKosten.insights.totalHours')} value={`${fmt1(data.totals.std_total)}h`} />
        <Kpi label={t('controllingZeitKosten.insights.billablePct')} value={`${fmt1(data.totals.billable_pct)}%`} />
        <Kpi label={t('controllingZeitKosten.insights.totalCost')} value={fmtEUR(data.totals.kosten_total)} />
        <Kpi label={t('controllingZeitKosten.insights.maCount')} value={fmt0(data.totals.ma_count)} />
        <Kpi label={t('controllingZeitKosten.insights.kundenCount')} value={fmt0(data.totals.kunden_count)} />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 16 }}>
        <Card title={t('controllingZeitKosten.insights.topKunden')}>
          {data.top_kunden.map((k, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: i < data.top_kunden.length - 1 ? '1px solid #F3F4F6' : 'none' }}>
              <span>{k.kunde_name}</span>
              <span style={{ fontWeight: 500 }}>{fmt1(k.stunden)}h · {fmtEUR(k.kosten)}</span>
            </div>
          ))}
        </Card>
        <Card title={t('controllingZeitKosten.insights.topMa')}>
          {data.top_mitarbeiter.map((m, i) => (
            <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: i < data.top_mitarbeiter.length - 1 ? '1px solid #F3F4F6' : 'none' }}>
              <span>{m.ma_name}</span>
              <span style={{ fontWeight: 500 }}>{fmt1(m.stunden)}h ({fmt1(m.stunden_billable)}h billable)</span>
            </div>
          ))}
        </Card>
        <Card title={t('controllingZeitKosten.insights.kategorien')}>
          <div style={{ height: 240 }}>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label={(e) => `${e.name}: ${fmt1(e.value)}h`}>
                  {pieData.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
                </Pie>
                <RechartsTooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>
    </div>
  );
}

// ────────────────────────── Tab 5: Was-waere-wenn-Calculator ──────────────────────────
function CalculatorTab({ t }) {
  const [defaults, setDefaults] = useState(null);
  const [maId, setMaId] = useState('');
  const [stundensatz, setStundensatz] = useState(27);
  const [stundenProMonat, setStundenProMonat] = useState(20);
  const [umsatzProMonat, setUmsatzProMonat] = useState(1500);
  const [verpackung, setVerpackung] = useState(0);
  const [versand, setVersand] = useState(0);

  useEffect(() => {
    api('/api/controlling/zeit-kosten/calculator-defaults')
      .then(d => { setDefaults(d); setStundensatz(d.default_stundensatz || 27); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (maId && defaults) {
      const ma = defaults.mitarbeiter.find(m => m.id === Number(maId));
      if (ma) setStundensatz(ma.stundensatz);
    }
  }, [maId, defaults]);

  const personalkosten = stundenProMonat * stundensatz;
  const gesamtKosten = personalkosten + Number(verpackung || 0) + Number(versand || 0);
  const db = umsatzProMonat - gesamtKosten;
  const margePct = umsatzProMonat > 0 ? (db / umsatzProMonat) * 100 : 0;

  return (
    <div>
      <div style={{ marginBottom: 16, fontSize: 13, color: 'var(--text-tertiary,#6B7280)' }}>
        {t('controllingZeitKosten.calc.hint')}
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 16 }}>
        <Card title={t('controllingZeitKosten.calc.inputs')}>
          <FormRow label={t('controllingZeitKosten.calc.ma')}>
            <select value={maId} onChange={(e) => setMaId(e.target.value)}
              style={{ width: '100%', padding: '6px 10px', border: '1px solid var(--border-input,#D1D5DB)', borderRadius: 6, fontSize: 13, background: 'var(--bg-card,#fff)' }}>
              <option value="">— {t('controllingZeitKosten.calc.maManual')} —</option>
              {defaults?.mitarbeiter.map(m => (
                <option key={m.id} value={m.id}>{m.name} (€ {fmt2(m.stundensatz)}{m.is_default ? ' *' : ''})</option>
              ))}
            </select>
          </FormRow>
          <FormRow label={t('controllingZeitKosten.calc.rate')}>
            <input type="number" step="0.5" value={stundensatz} onChange={(e) => setStundensatz(Number(e.target.value))} style={inputStyle} /> €/h
          </FormRow>
          <FormRow label={t('controllingZeitKosten.calc.hoursPerMonth')}>
            <input type="number" step="0.5" value={stundenProMonat} onChange={(e) => setStundenProMonat(Number(e.target.value))} style={inputStyle} /> h
          </FormRow>
          <FormRow label={t('controllingZeitKosten.calc.revenuePerMonth')}>
            <input type="number" step="10" value={umsatzProMonat} onChange={(e) => setUmsatzProMonat(Number(e.target.value))} style={inputStyle} /> €
          </FormRow>
          <FormRow label={t('controllingZeitKosten.calc.packaging')}>
            <input type="number" step="10" value={verpackung} onChange={(e) => setVerpackung(Number(e.target.value))} style={inputStyle} /> €
          </FormRow>
          <FormRow label={t('controllingZeitKosten.calc.shipping')}>
            <input type="number" step="10" value={versand} onChange={(e) => setVersand(Number(e.target.value))} style={inputStyle} /> €
          </FormRow>
        </Card>
        <Card title={t('controllingZeitKosten.calc.result')}>
          <ResultRow label={t('controllingZeitKosten.calc.staffCost')} value={fmtEUR(personalkosten)} />
          <ResultRow label={t('controllingZeitKosten.calc.totalCost')} value={fmtEUR(gesamtKosten)} />
          <ResultRow label={t('controllingZeitKosten.calc.revenue')} value={fmtEUR(umsatzProMonat)} />
          <div style={{ borderTop: '1px solid var(--border-card,#E5E7EB)', margin: '12px 0' }} />
          <ResultRow
            label={t('controllingZeitKosten.calc.db')}
            value={fmtEUR(db)}
            big
            color={db < 0 ? '#991B1B' : '#065F46'}
          />
          <ResultRow
            label={t('controllingZeitKosten.calc.margin')}
            value={`${fmt1(margePct)}%`}
            big
            color={margePct < 0 ? '#991B1B' : margePct < 15 ? '#92400E' : '#065F46'}
          />
          <div style={{ marginTop: 16, padding: 12, background: 'var(--bg-inset,#F8F9FC)', borderRadius: 8, fontSize: 12, color: 'var(--text-tertiary,#6B7280)' }}>
            <div style={{ marginBottom: 6 }}>{t('controllingZeitKosten.calc.formula')}</div>
            <code style={{ fontSize: 11 }}>DB = Umsatz − (h × €/h) − Verpackung − Versand</code>
          </div>
        </Card>
      </div>
    </div>
  );
}

// ────────────────────────── Helpers ──────────────────────────
function Kpi({ label, value }) {
  return (
    <div style={{ padding: '14px 18px', border: '1px solid var(--border-card,#E5E7EB)', borderRadius: 12, background: 'var(--bg-card,#fff)' }}>
      <div style={{ fontSize: 12, color: 'var(--text-tertiary,#6B7280)', marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--text-primary,#1A1A2E)', lineHeight: 1.2 }}>{value}</div>
    </div>
  );
}

function Card({ title, children }) {
  return (
    <div style={{ padding: '16px 20px', border: '1px solid var(--border-card,#E5E7EB)', borderRadius: 12, background: 'var(--bg-card,#fff)' }}>
      <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12, color: 'var(--text-primary,#1A1A2E)' }}>{title}</div>
      {children}
    </div>
  );
}

function FormRow({ label, children }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <label style={{ display: 'block', fontSize: 12, color: 'var(--text-secondary,#4B5563)', marginBottom: 4 }}>{label}</label>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>{children}</div>
    </div>
  );
}

function ResultRow({ label, value, big, color }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: big ? '8px 0' : '4px 0' }}>
      <span style={{ fontSize: big ? 14 : 13, color: 'var(--text-secondary,#4B5563)' }}>{label}</span>
      <span style={{ fontSize: big ? 18 : 14, fontWeight: big ? 700 : 500, color: color || 'var(--text-primary,#1A1A2E)' }}>{value}</span>
    </div>
  );
}

function SegBtn({ active, onClick, children }) {
  return (
    <button onClick={onClick}
      style={{
        padding: '6px 14px', borderRadius: 8,
        border: '1px solid ' + (active ? '#338edc' : 'var(--border-card,#E5E7EB)'),
        background: active ? '#338edc' : 'var(--bg-card,#fff)',
        color: active ? '#fff' : 'var(--text-secondary,#4B5563)',
        fontSize: 13, fontWeight: 500, cursor: 'pointer',
      }}>
      {children}
    </button>
  );
}

function Th({ children, align }) {
  return <th style={{ padding: '10px 12px', textAlign: align || 'left', fontSize: 12, fontWeight: 600, color: 'var(--text-secondary,#4B5563)' }}>{children}</th>;
}
function Td({ children, align }) {
  return <td style={{ padding: '10px 12px', textAlign: align || 'left', fontSize: 13, color: 'var(--text-primary,#1A1A2E)' }}>{children}</td>;
}

const inputStyle = {
  flex: 1,
  padding: '6px 10px',
  border: '1px solid var(--border-input,#D1D5DB)',
  borderRadius: 6,
  fontSize: 13,
  background: 'var(--bg-card,#fff)',
};

function EmptyState({ t }) {
  return (
    <div style={{ padding: 48, textAlign: 'center', border: '1px solid var(--border-card,#E5E7EB)', borderRadius: 12, background: 'var(--bg-card,#fff)' }}>
      <div style={{ fontSize: 14, color: 'var(--text-tertiary,#6B7280)', marginBottom: 4 }}>
        {t('controllingZeitKosten.empty.title')}
      </div>
      <div style={{ fontSize: 12, color: 'var(--text-muted,#9CA3AF)' }}>
        {t('controllingZeitKosten.empty.hint')}
      </div>
    </div>
  );
}

// ────────────────────────── Main ──────────────────────────
export default function ControllingZeitKosten() {
  const { t } = useLanguage();
  const [tab, setTab] = useState('heatmap');
  const [range, setRange] = useState(defaultRange());
  const [reloadKey, setReloadKey] = useState(0);

  return (
    <div style={{ padding: '24px 32px', maxWidth: 1600, margin: '0 auto' }}>
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--text-primary,#1A1A2E)', margin: 0 }}>
          {t('controllingZeitKosten.title')}
        </h1>
        <div style={{ fontSize: 13, color: 'var(--text-tertiary,#6B7280)', marginTop: 4 }}>
          {t('controllingZeitKosten.subtitle')}
        </div>
      </div>

      {/* Date Range + Refresh */}
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' }}>
        <label style={{ fontSize: 12, color: 'var(--text-tertiary,#6B7280)' }}>
          {t('controllingZeitKosten.range.from')}
          <input type="date" value={range.von} onChange={(e) => setRange({ ...range, von: e.target.value })}
            style={{ marginLeft: 8, padding: '4px 8px', border: '1px solid var(--border-input,#D1D5DB)', borderRadius: 6, fontSize: 13, background: 'var(--bg-card,#fff)' }} />
        </label>
        <label style={{ fontSize: 12, color: 'var(--text-tertiary,#6B7280)' }}>
          {t('controllingZeitKosten.range.to')}
          <input type="date" value={range.bis} onChange={(e) => setRange({ ...range, bis: e.target.value })}
            style={{ marginLeft: 8, padding: '4px 8px', border: '1px solid var(--border-input,#D1D5DB)', borderRadius: 6, fontSize: 13, background: 'var(--bg-card,#fff)' }} />
        </label>
        <button onClick={() => setReloadKey(k => k + 1)}
          style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid var(--border-card,#E5E7EB)', background: 'var(--bg-card,#fff)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
          <RefreshCw size={14} /> {t('controllingZeitKosten.refresh')}
        </button>
      </div>

      {/* Tab Bar */}
      <div style={{
        display: 'inline-flex', gap: 2, padding: 3,
        background: 'var(--bg-inset,#F8F9FC)', borderRadius: 10,
        border: '1px solid var(--border-card,#E5E7EB)',
        marginBottom: 20,
      }}>
        {TABS.map(tabDef => (
          <button key={tabDef.key} onClick={() => setTab(tabDef.key)}
            style={{
              padding: '8px 16px', borderRadius: 8, border: 'none',
              background: tab === tabDef.key ? 'var(--bg-card,#fff)' : 'transparent',
              color: tab === tabDef.key ? 'var(--text-primary,#1A1A2E)' : 'var(--text-secondary,#4B5563)',
              fontSize: 13, fontWeight: 500, cursor: 'pointer',
              boxShadow: tab === tabDef.key ? '0 1px 2px rgba(0,0,0,0.05)' : 'none',
            }}>
            {t(tabDef.labelKey)}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div key={`${tab}-${reloadKey}`}>
        {tab === 'heatmap'    && <HeatmapTab t={t} range={range} />}
        {tab === 'ma'         && <MaTab t={t} range={range} />}
        {tab === 'trends'     && <TrendsTab t={t} range={range} />}
        {tab === 'insights'   && <InsightsTab t={t} range={range} />}
        {tab === 'calculator' && <CalculatorTab t={t} />}
      </div>
    </div>
  );
}
